/**
 * FolderScanProgressChip — the floating "minimized" surface for a desktop
 * folder-scan ingest. Mounted once at app root (inside FolderScanJobProvider).
 *
 * When the user hits "Add" in FolderScanSheet the sheet closes and the work
 * continues in the background; this chip is what they see instead of a blocked
 * modal: a small pill, bottom-right, showing "x of y" + a percentage while files
 * upload and then while Ellie's backend reads them (driven by Ably completions).
 * Tap it to expand the full per-file list (FolderScanJobSheet); dismiss any time;
 * auto-clears a few seconds after it finishes.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../hooks/useTheme';
import Icon from './Icon';
import { isElectron } from '../utilities/platform';
import { useFolderScanJob } from '../contexts/FolderScanJobContext';
import FolderScanJobSheet from './FolderScanJobSheet';

/** How long the "done" pill lingers before it clears itself. */
const DONE_LINGER_MS = 9000;

export default function FolderScanProgressChip() {
  const { job, dismiss } = useFolderScanJob();
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const styles = makeStyles(theme);
  const [detailOpen, setDetailOpen] = useState(false);

  // Auto-clear shortly after the job finishes — but never while the user has the
  // detail sheet open reading the results.
  useEffect(() => {
    if (job?.status !== 'done' || detailOpen) return;
    const t = setTimeout(() => dismiss(), DONE_LINGER_MS);
    return () => clearTimeout(t);
  }, [job?.status, detailOpen, dismiss]);

  if (!isElectron || !job) return null;

  const who = job.projectName?.trim() ? job.projectName.trim() : 'this plan';
  const done = job.status === 'done';
  const uploading = job.status === 'uploading';

  const fraction = uploading
    ? (job.total ? job.uploaded / job.total : 0)
    : (job.total ? (job.processed + job.failed) / job.total : 0);
  const pct = Math.max(0, Math.min(100, Math.round(fraction * 100)));

  let title: string;
  let subtitle: string;
  if (uploading) {
    title = `Adding to ${who}`;
    subtitle = `${job.uploaded} of ${job.total} uploaded`;
  } else if (job.status === 'ingesting') {
    title = `Reading ${who}'s files`;
    subtitle = `${job.processed} of ${job.total} processed`;
  } else {
    title = `Added ${job.processed} to ${who}`;
    subtitle = job.failed > 0 ? `${job.failed} couldn't be added` : 'Ellie finished reading them';
  }

  return (
    <>
      <View pointerEvents="box-none" style={[styles.wrap, { bottom: (insets.bottom || 0) + 16 }]}>
        <View style={[styles.chip, { backgroundColor: theme.colors.card, borderColor: theme.colors.border }]}>
          <View style={styles.row}>
            <Pressable style={styles.tapArea} onPress={() => setDetailOpen(true)}>
              <View style={[styles.iconWrap, { backgroundColor: theme.colors.primary + '18' }]}>
                {done
                  ? <Icon icon={['fad', 'circle-check']} size={16} color={theme.colors.primary} />
                  : <ActivityIndicator size="small" color={theme.colors.primary} />}
              </View>
              <View style={styles.text}>
                <Text style={[styles.title, { color: theme.colors.text }]} numberOfLines={1}>{title}</Text>
                <Text style={[styles.sub, { color: theme.colors.muted }]} numberOfLines={1}>
                  {subtitle}{!done ? ` · ${pct}%` : ''}
                </Text>
              </View>
              <Icon icon={['fas', 'chevron-up']} size={12} color={theme.colors.muted} />
            </Pressable>

            <TouchableOpacity onPress={dismiss} style={styles.closeBtn} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Icon icon={['fas', 'xmark']} size={14} color={theme.colors.muted} />
            </TouchableOpacity>
          </View>

          <View style={[styles.track, { backgroundColor: theme.colors.border }]}>
            <View style={[styles.fill, { backgroundColor: theme.colors.primary, width: `${done ? 100 : pct}%` }]} />
          </View>
        </View>
      </View>

      <FolderScanJobSheet
        visible={detailOpen}
        onClose={() => setDetailOpen(false)}
        job={job}
        onDismissJob={() => { setDetailOpen(false); dismiss(); }}
      />
    </>
  );
}

const makeStyles = (theme: ReturnType<typeof useTheme>) =>
  StyleSheet.create({
    wrap: {
      position: 'absolute',
      right: 16,
      left: 16,
      alignItems: 'flex-end',
      zIndex: 1000,
    },
    chip: {
      width: 300,
      maxWidth: '100%',
      borderRadius: 14,
      borderWidth: StyleSheet.hairlineWidth,
      paddingTop: 12,
      paddingBottom: 10,
      paddingHorizontal: 14,
      shadowColor: '#000',
      shadowOpacity: 0.18,
      shadowRadius: 16,
      shadowOffset: { width: 0, height: 8 },
      elevation: 8,
    },
    row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    tapArea: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 11 },
    iconWrap: { width: 32, height: 32, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
    text: { flex: 1 },
    title: { fontSize: 14, fontWeight: '600' },
    sub: { fontSize: 12, marginTop: 1 },
    closeBtn: { paddingLeft: 4 },
    track: { height: 5, borderRadius: 999, overflow: 'hidden', marginTop: 10 },
    fill: { height: '100%', borderRadius: 999 },
  });
